type GeometryProperties = Record<string, unknown>

export interface UserGeometryInfoItem {
  key: string
  label: string
  value: string
}

interface ExtractUserGeometryInfoItemsOptions {
  yesLabel: string
  noLabel: string
}

interface HtmlValueObject {
  '@type'?: unknown
  value?: unknown
}

const HIDDEN_PROPERTY_KEYS = new Set([
  'id',
  'hidden',
  'showLabel',
  'labelMinZoom',
  'stroke',
  'stroke-opacity',
  'stroke-width',
  'fill',
  'fill-opacity',
  'icon',
  'marker-color',
  'marker-size',
  'line-style'
])

const HIDDEN_PREFIXES = ['icon-', 'label-']

export const filterUserGeometryDisplayProperties = (
  properties: GeometryProperties | null | undefined
): GeometryProperties | null => {
  if (!properties || typeof properties !== 'object') {
    return null
  }

  const filteredEntries = Object.entries(properties).filter(([, value]) => value !== null && value !== undefined)
  if (!filteredEntries.length) {
    return null
  }

  return Object.fromEntries(filteredEntries)
}

const normalizeKeyLabel = (key: string) => {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toUpperCase()
}

const sanitizeHtmlText = (value: string): string | null => {
  const withLineBreaks = value.replace(/<br\s*\/?>/gi, '\n')
  const withoutTags = withLineBreaks.replace(/<\/?[^>]+>/g, '')
  const normalized = withoutTags
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

  return normalized.length ? normalized : null
}

const isTypedValueObject = (value: unknown): value is HtmlValueObject => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  return Object.prototype.hasOwnProperty.call(value, '@type') || Object.prototype.hasOwnProperty.call(value, 'value')
}

// Mapbox GL JSON-stringifies object/array property values when features are read
// back from click/query events, so a property that started as
// `{ "@type": "html", value: "..." }` arrives here as a JSON string. Decode it
// back so the typed-value/html handling below can run. Only objects/arrays are
// re-interpreted; a parsed primitive (e.g. `"true"` → true) keeps the original
// string to avoid reinterpreting plain text that merely parses as JSON.
const parseJsonObjectString = (value: string): unknown | null => {
  const trimmed = value.trim()
  if (trimmed[0] !== '{' && trimmed[0] !== '[') {
    return null
  }

  try {
    const parsed = JSON.parse(trimmed)
    return parsed !== null && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

const stringifyValue = (value: unknown, { yesLabel, noLabel }: ExtractUserGeometryInfoItemsOptions): string | null => {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'string') {
    const normalized = value.trim()
    if (normalized.length === 0) {
      return null
    }

    const parsed = parseJsonObjectString(normalized)
    if (parsed !== null) {
      return stringifyValue(parsed, { yesLabel, noLabel })
    }

    return normalized
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : null
  }

  if (typeof value === 'boolean') {
    return value ? yesLabel : noLabel
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return null
    }

    if (value.every((item) => ['string', 'number', 'boolean'].includes(typeof item))) {
      return value
        .map((item) => stringifyValue(item, { yesLabel, noLabel }))
        .filter(Boolean)
        .join(', ')
    }

    return JSON.stringify(value, null, 2)
  }

  if (typeof value === 'object') {
    if (isTypedValueObject(value)) {
      if (!Object.prototype.hasOwnProperty.call(value, 'value')) {
        return null
      }

      if (value['@type'] === 'html' && typeof value.value === 'string') {
        return sanitizeHtmlText(value.value)
      }

      return stringifyValue(value.value, { yesLabel, noLabel })
    }

    const entries = Object.entries(value)
    if (!entries.length) {
      return null
    }

    return JSON.stringify(value, null, 2)
  }

  return null
}

export const extractUserGeometryInfoItems = (
  properties: GeometryProperties | null | undefined,
  options: ExtractUserGeometryInfoItemsOptions
): UserGeometryInfoItem[] => {
  const filteredProperties = filterUserGeometryDisplayProperties(properties)
  if (!filteredProperties) {
    return []
  }

  return Object.entries(filteredProperties).reduce<UserGeometryInfoItem[]>((items, [key, value]) => {
    if (
      key.startsWith('__') ||
      HIDDEN_PROPERTY_KEYS.has(key) ||
      HIDDEN_PREFIXES.some((prefix) => key.startsWith(prefix))
    ) {
      return items
    }

    const stringifiedValue = stringifyValue(value, options)
    if (!stringifiedValue) {
      return items
    }

    items.push({
      key,
      label: normalizeKeyLabel(key),
      value: stringifiedValue
    })

    return items
  }, [])
}
